import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useModalStore } from '@/store';
import { PLUS_COLORS } from '@/constants/Colors';
import {
  API_DAILY_LIMIT_PREMIUM_DISPLAY,
  PLUS_MONTHLY_CREDITS_DISPLAY,
  PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY,
  PRICING_PLANS,
} from '@/constants/config';
import { trackEvent } from '@/constants/utils/analytics';

const BENEFITS = [
  { icon: 'briefcase', boldText: '10 Work sessions at once', text: '— up from 3 on Plus' },
  { icon: 'coins', boldText: `${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits/month`, text: `— 6× the ${PLUS_MONTHLY_CREDITS_DISPLAY} you get today` },
  { icon: 'gauge-high', boldText: `${API_DAILY_LIMIT_PREMIUM_DISPLAY} API requests/day`, text: '— 5× higher rate limits' },
  { icon: 'headset', boldText: 'Priority email support', text: '— your emails jump the queue' },
];

const createStyles = (colors) => StyleSheet.create({
  container: {
    ...(Platform.OS === 'macos' && { width: '100%', flexShrink: 0 }),
    backgroundColor: PLUS_COLORS.backgroundColorPurple,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: PLUS_COLORS.textColorPurple,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    color: colors.text_color_primary,
    flex: 1,
  },
  benefitsList: {
    gap: 10,
    marginBottom: 16,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  benefitIcon: {
    width: 22,
    height: 22,
    borderRadius: 7,
    backgroundColor: '#7C3AED',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  benefitTextContainer: {
    flex: 1,
  },
  buttonContainer: {
    marginTop: 4,
  },
});

function PremiumUpsellBanner({ onClose }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = useCallback(async () => {
    setIsLoading(true);
    trackEvent('conversion', 'premium_upsell_clicked', 'credits_modal_banner');
    try {
      onClose?.();
      closeModal();
      // PricingModal detects the active Plus subscription and opens in its
      // Premium-upgrade state (Plus card hidden, Premium preselected).
      setTimeout(() => openModal('PricingModal'), 100);
    } finally {
      setIsLoading(false);
    }
  }, [openModal, onClose, closeModal]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon name="gem" size={18} color={PLUS_COLORS.textColorPurple} solid style={{ marginTop: 3 }} />
        <Text size="l" weight="bold" style={styles.title}>
          {`NEED MORE THAN ${PLUS_MONTHLY_CREDITS_DISPLAY} CREDITS A MONTH?`}
        </Text>
      </View>

      <View style={styles.benefitsList}>
        {BENEFITS.map((f) => (
          <View key={f.icon} style={styles.benefitItem}>
            <View style={styles.benefitIcon}>
              <Icon name={f.icon} size={11} color="#FFFFFF" solid />
            </View>
            <View style={styles.benefitTextContainer}>
              <Text size="n" weight="bold">{f.boldText}</Text>
              <Text size="s" color="secondary">{f.text}</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={styles.buttonContainer}>
        <Button
          variant="plus"
          text={Platform.OS === 'macos' ? 'Upgrade to Plus Premium' : `Upgrade to Plus Premium - $${PRICING_PLANS.plusPremium.annualMonthlyPrice}/mo billed annually`}
          prependIcon="gem"
          onPress={handleUpgrade}
          isLoading={isLoading}
          disabled={isLoading}
          isFullWidth={Platform.OS !== 'macos'}
        />
        {Platform.OS === 'macos' && <Text size="s" color="secondary" style={{ textAlign: 'center', marginTop: 8 }}>${PRICING_PLANS.plusPremium.annualMonthlyPrice}/mo billed annually</Text>}
      </View>
    </View>
  );
}

export default React.memo(PremiumUpsellBanner);
